// THE ONE SCAN ANSWER — his standard, 2026-10-16:
//   "ACCEPTED: the green tint flash and the accept sound. REFUSED: the red flash and the buzz,
//    with plain words saying why. The same look and sound on every screen that takes a scan.
//    One shared piece, not copied screen by screen, so they always match."
//
// Every screen already called beepSuccess()/beepError() at exactly the right moment, so those two
// names now LIVE HERE and carry the whole answer: sound + full-screen tint + haptic (+ spoken word
// where a screen asks for one). Nothing can beep without flashing, and nothing can flash without
// beeping, because there is only one implementation.
//
// The overlay is mounted ONCE by <ScanFeedbackHost /> in App.js, so a screen needs no JSX of its own.
import { useEffect, useState } from "react";
import { Check, X, AlertTriangle } from "lucide-react";

import { haptic, HAPTIC } from "@/lib/floor";
import { beepAccept, beepRefuse, speak, isSoundOn } from "@/lib/sound";
import { noteScanAnswer, onNextAction, clearWarnings, nextStep, REFUSAL_FRESH_MS } from "@/lib/refusals";
import "@/floor/floor.css";

const TONE = {
  success: { bg: "bg-emerald-500", Icon: Check, hold: 900, anim: "floor-flash" },
  error: { bg: "bg-red-500", Icon: X, hold: 1400, anim: "floor-flash-long" },
  warning: { bg: "bg-amber-500", Icon: AlertTriangle, hold: 1400, anim: "floor-flash-long" },
};

let _show = null;          // set by the host once it is mounted
let _seq = 0;

function fire(tone, message, say) {
  (tone === "success" ? beepAccept : beepRefuse)();
  haptic(tone === "success" ? HAPTIC.success : HAPTIC.error);
  if (say) speak(say);
  clearWarnings();                       // a new scan answer is the "next scan" that clears a yellow warning
  if (tone !== "success") noteScanAnswer(message);
  if (_show) _show({ tone, message: message || "", ts: ++_seq, at: Date.now() });
}

// ACCEPTED — green tint + accept sound. A message is optional; pass what was scanned if you have it.
export function scanAccept(message, opts) { fire("success", message, opts?.say); }
// REFUSED — red tint + buzz. ALWAYS pass plain words saying why.
export function scanRefuse(message, opts) { fire("error", message, opts?.say); }
// NOT WRONG, BUT NOT NEW — amber tint + buzz ("already here", "already received").
export function scanWarn(message, opts) { fire("warning", message, opts?.say); }

export { speak, isSoundOn };

// Sound WITHOUT a flash. Only for the rare case where a dialog or a warning panel is already
// opening on top of the screen and a full-screen tint would fight it.
export function scanSoundOnly(ok) {
  (ok ? beepAccept : beepRefuse)();
  haptic(ok ? HAPTIC.success : HAPTIC.error);
}

export function useScanFeedback() {
  return { accept: scanAccept, refuse: scanRefuse, warn: scanWarn };
}

// The tint is the instant answer; a RED refusal then STAYS as a card until it is tapped away, and a YELLOW
// warning stays until it is tapped or the next scan / action (his rule, 30 Oct). Successes fade as before.
export function ScanFeedbackHost() {
  const [flash, setFlash] = useState(null);
  const [held, setHeld] = useState([]);
  useEffect(() => {
    _show = (f) => {
      setFlash(f);
      if (f.tone !== "success" && f.message) setHeld((h) => [...h, f].slice(-4));
    };
    const off = onNextAction((now) => setHeld((h) => h.filter((x) => x.tone === "error" || now - x.at < REFUSAL_FRESH_MS)));
    return () => { _show = null; off(); };
  }, []);
  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), TONE[flash.tone].hold);
    return () => clearTimeout(t);
  }, [flash]);
  const dismiss = (ts) => setHeld((h) => h.filter((x) => x.ts !== ts));
  return (
    <>
      {flash && <FlashTint flash={flash} />}
      {held.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-[96] flex flex-col items-center gap-2 p-3 pointer-events-none" data-testid="scan-held-stack">
          {held.map((h) => <HeldCard key={h.ts} item={h} onDismiss={() => dismiss(h.ts)} />)}
        </div>
      )}
    </>
  );
}

function HeldCard({ item, onDismiss }) {
  const red = item.tone === "error";
  const kind = red ? "refusal" : "warning";
  return (
    <div role="alert" data-refusal-card={kind} data-testid={`scan-${kind}-card`} onClick={onDismiss}
      className={`pointer-events-auto w-full max-w-2xl cursor-pointer rounded-2xl border-4 shadow-2xl px-5 py-4 flex items-start gap-3 ${red ? "bg-red-600 border-red-800 text-white" : "bg-amber-300 border-amber-500 text-slate-950"}`}>
      {red ? <X className="w-9 h-9 shrink-0" strokeWidth={3} /> : <AlertTriangle className="w-9 h-9 shrink-0" strokeWidth={2.5} />}
      <div className="min-w-0 flex-1">
        <div data-testid={`scan-${kind}-message`} className="text-xl sm:text-2xl font-black leading-tight break-words">{item.message}</div>
        {red && nextStep(item.message, { scan: true }) && (
          <div data-testid="scan-refusal-next-step" className="mt-1 text-base sm:text-lg font-semibold leading-snug">{nextStep(item.message, { scan: true })}</div>
        )}
        <div className={`mt-1 text-sm font-bold uppercase tracking-wide ${red ? "text-red-100" : "text-amber-900"}`}>
          {red ? "Tap to close" : "Tap to close — or just scan the next one"}
        </div>
      </div>
      <button type="button" data-testid={`scan-${kind}-dismiss`} aria-label="Close" onClick={(e) => { e.stopPropagation(); onDismiss(); }}
        className={`shrink-0 h-12 px-4 rounded-xl font-black text-lg ${red ? "bg-white text-red-700" : "bg-slate-950 text-amber-300"}`}>OK</button>
    </div>
  );
}

function FlashTint({ flash }) {
  const t = TONE[flash.tone];
  return (
    <div
      key={flash.ts}
      data-testid={`floor-flash-${flash.tone}`}
      data-scan-flash={flash.tone === "success" ? "accepted" : flash.tone === "error" ? "refused" : "already"}
      className={`fixed inset-0 z-[95] flex items-center justify-center pointer-events-none ${t.bg} ${t.anim}`}
    >
      <div className="text-center text-white px-6">
        <t.Icon className="w-24 h-24 mx-auto mb-3" strokeWidth={2.5} />
        {flash.message && (
          <div data-testid="scan-flash-message" className="text-3xl md:text-4xl font-black tracking-tight">
            {flash.message}
          </div>
        )}
      </div>
    </div>
  );
}
